<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, FormInput, UiModalHeader } from '@/shared/ui';
  import DirectoryIconPicker from './DirectoryIconPicker.vue';
  import { DIRECTORY_TITLE_MAX_LENGTH } from '../config/directoryTitle';
  import {
    type CreateModuleSubmitPayload,
    useCreateModuleForm,
  } from '../model/useCreateModuleForm';

  const props = withDefaults(
    defineProps<{
      isSubmitting?: boolean;
      titleError?: string;
    }>(),
    {
      isSubmitting: false,
      titleError: '',
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'clearTitleError'): void;
    (e: 'submit', payload: CreateModuleSubmitPayload): void;
  }>();

  const title = ref('');
  const iconId = ref('');

  const { reset, buildSubmitPayload, isTitleValid } = useCreateModuleForm(title, iconId);

  const isSubmitDisabled = computed(
    () => !isTitleValid.value || props.isSubmitting
  );

  const handleTitleInput = (value: string) => {
    title.value = value;
    if (props.titleError) {
      emit('clearTitleError');
    }
  };

  const handleClose = () => {
    emit('close');
  };

  const handleSubmit = () => {
    if (isSubmitDisabled.value) {
      return;
    }
    emit('submit', buildSubmitPayload());
  };

  const handleClosed = () => {
    reset();
  };
</script>

<template>
  <VueFinalModal
    class="flex items-center justify-center"
    content-class="flex w-[600px] max-w-[calc(100vw-2rem)] flex-col rounded-2xl bg-white-01 shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    click-to-close
    @closed="handleClosed"
  >
    <div
      data-testid="create-module-modal"
      class="flex flex-col"
    >
      <UiModalHeader
        class="flex-shrink-0 border-b border-gray-02 px-6 py-4 text-sm"
        title="Criar Módulo"
        @close="handleClose"
      />

      <div
        data-testid="create-module-fields-row"
        class="flex items-start gap-3 px-6 py-6"
      >
        <div class="min-w-0 flex-1">
          <FormInput
            id="create-module-title"
            label="Título do Módulo"
            type="text"
            :model-value="title"
            placeholder="Escreva um título que represente o Módulo"
            :max-length="DIRECTORY_TITLE_MAX_LENGTH"
            :error="titleError"
            @update:model-value="handleTitleInput"
          />
        </div>

        <div class="mt-8 w-[140px] shrink-0">
          <DirectoryIconPicker v-model="iconId" />
        </div>
      </div>

      <div class="flex flex-shrink-0 justify-end gap-3 border-t border-gray-02 px-6 py-4">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          :disabled="isSubmitting"
          @click="handleClose"
        />
        <ColoredButton
          data-testid="create-module-submit"
          text="Criar"
          :disabled="isSubmitDisabled"
          @click="handleSubmit"
        />
      </div>
    </div>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
